import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './HeroSectionCarousel.module.css';

// --- Configuration for Particle Effect ---
const GRID_ROWS = 10;
const GRID_COLS = 15;
const PARTICLE_ANIMATION_DURATION = 800;
const STAGGER_DELAY_MAX = 500;
const AUTOPLAY_INTERVAL = 5000;
const MOBILE_BREAKPOINT = 768; // pixels, adjust as needed

// --- Image Data ---
// Now an array of objects, each with desktop and mobile URLs
const defaultImages = [
  {
    desktop: 'https://res.cloudinary.com/ddkyeuhk8/image/upload/v1707499518/qfoechqdcg04kl9zo4ix.png', // Example desktop
    mobile: 'https://res.cloudinary.com/ddkyeuhk8/image/upload/v1747503078/Copy%20of%20DS%20banner/wmr3tupmeqdumchctyqk.png' // Example mobile (can be a different image or resized version)
  },
  {
    desktop: 'https://res.cloudinary.com/ddkyeuhk8/image/upload/v1707499516/pdflzxb0leln46gdqhpp.png',
    mobile: 'https://res.cloudinary.com/ddkyeuhk8/image/upload/v1747503076/Copy%20of%20DS%20banner/orp1o0qbwupvbjftahpp.png'
  },
  {
    desktop: 'https://res.cloudinary.com/ddkyeuhk8/image/upload/v1707499516/tuppjqp2gh269mgg0l0l.png',
    mobile: 'https://res.cloudinary.com/ddkyeuhk8/image/upload/v1747503076/Copy%20of%20DS%20banner/zwgutsyyunq5vi3cyuep.png'
  },
];

const HeroSectionCarousel = ({ images = defaultImages }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [particles, setParticles] = useState([]);
  const autoplayTimerRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(false);

  const totalImages = images.length;

  // --- Screen Size Detection ---
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < MOBILE_BREAKPOINT);
    };
    checkScreenSize(); // Initial check
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // --- Helper to get current image URL based on screen size ---
  const getCurrentImageUrl = useCallback((imageObject) => {
    if (!imageObject) return '';
    return isMobileView ? imageObject.mobile : imageObject.desktop;
  }, [isMobileView]);

  // --- Particle Generation ---
  const generateParticles = useCallback((imageObject) => {
    if (!imageObject) return;
    const imageUrl = getCurrentImageUrl(imageObject);
    if (!imageUrl) return;

    const newParticles = [];
    for (let i = 0; i < GRID_ROWS; i++) {
      for (let j = 0; j < GRID_COLS; j++) {
        const delay = Math.random() * STAGGER_DELAY_MAX;
        const translateX = (Math.random() - 0.5) * 50;
        const translateY = (Math.random() - 0.5) * 50;

        newParticles.push({
          id: `particle-${i}-${j}`,
          style: {
            backgroundImage: `url(${imageUrl})`,
            backgroundPosition: `${(j / (GRID_COLS - 1)) * 100}% ${(i / (GRID_ROWS - 1)) * 100}%`,
            backgroundSize: `${GRID_COLS * 100}% ${GRID_ROWS * 100}%`,
            animationDelay: `${delay}ms`,
            '--translateX': `${translateX}px`,
            '--translateY': `${translateY}px`,
          },
        });
      }
    }
    setParticles(newParticles);
  }, [getCurrentImageUrl]); // Depend on getCurrentImageUrl which depends on isMobileView


  // --- Navigation Logic ---
  const goToSlide = useCallback((newIndex) => {
    if (isTransitioning || newIndex === currentIndex || !images[currentIndex]) return;

    setIsTransitioning(true);
    setPrevIndex(currentIndex);

    // Generate particles for the outgoing image (pass the image object)
    generateParticles(images[currentIndex]);

    setCurrentIndex(newIndex);

    const totalTransitionTime = PARTICLE_ANIMATION_DURATION + STAGGER_DELAY_MAX + 100;
    setTimeout(() => {
      setIsTransitioning(false);
      setParticles([]);
    }, totalTransitionTime);
  }, [currentIndex, images, isTransitioning, generateParticles]);


  const nextSlide = useCallback(() => {
    const newIndex = (currentIndex + 1) % totalImages;
    goToSlide(newIndex);
  }, [currentIndex, totalImages, goToSlide]);

  const prevSlide = useCallback(() => {
    const newIndex = (currentIndex - 1 + totalImages) % totalImages;
    goToSlide(newIndex);
  }, [currentIndex, totalImages, goToSlide]);


  // --- Autoplay ---
  useEffect(() => {
    const startAutoplay = () => {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
      }
      autoplayTimerRef.current = setTimeout(() => {
        nextSlide();
      }, AUTOPLAY_INTERVAL);
    };

    if (!isTransitioning && totalImages > 1) { // Only autoplay if more than 1 image
      startAutoplay();
    }

    return () => {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current);
      }
    };
  }, [nextSlide, isTransitioning, totalImages]);

  const handleMouseEnter = () => {
    if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
  };
  const handleMouseLeave = () => {
    if (!isTransitioning && totalImages > 1) {
       autoplayTimerRef.current = setTimeout(() => {
        nextSlide();
      }, AUTOPLAY_INTERVAL);
    }
  };


  // --- Render ---
  if (images.length === 0) {
    return <div className={styles.carouselContainer}>No images to display.</div>;
  }

  return (
    <div
      className={styles.carouselContainer}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {images.map((imageObject, index) => (
        <div
          key={imageObject.desktop || imageObject.mobile || index} // Use a unique part of the image or index as key
          className={`${styles.slide} ${index === currentIndex ? styles.slideActive : ''}`}
          style={{
            backgroundImage: `url(${getCurrentImageUrl(imageObject)})`,
            opacity: index === currentIndex ? 1 : 0,
            visibility: index === currentIndex ? 'visible' : 'hidden',
            transition: 'none'
          }}
        />
      ))}

      {isTransitioning && particles.length > 0 && (
        <div
          className={styles.particleOverlay}
          style={{
            gridTemplateRows: `repeat(${GRID_ROWS}, 1fr)`,
            gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
          }}
        >
          {particles.map((particle) => (
            <div
              key={particle.id}
              className={`${styles.particle} ${styles.particleAnimating}`}
              style={particle.style}
            />
          ))}
        </div>
      )}

      {totalImages > 1 && ( // Only show controls if more than 1 image
        <>
          <button onClick={prevSlide} className={`${styles.arrow} ${styles.prevArrow}`} aria-label="Previous slide">
            ❮
          </button>
          <button onClick={nextSlide} className={`${styles.arrow} ${styles.nextArrow}`} aria-label="Next slide">
            ❯
          </button>
          <div className={styles.dots}>
            {images.map((_, index) => (
              <button
                key={`dot-${index}`}
                className={`${styles.dot} ${index === currentIndex ? styles.dotActive : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroSectionCarousel;